/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow-restricted-names */
/* eslint-disable no-param-reassign */
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { api } from './api';

const threadsAdapter = createEntityAdapter({
  sortComparer: (a, b) => b.createdAt.localeCompare(a.createdAt),
});

const threadsInitialState = threadsAdapter.getInitialState();

export const threadsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createThread: builder.mutation({
      query: ({ title, body, category }) => ({
        url: '/threads',
        method: 'POST',
        body: { title, body, category },
      }),
      transformResponse: (response) => response.data.thread,
      invalidatesTags: ['Thread', 'Leaderboard'],
      transformErrorResponse: (response) => response.data,
    }),

    getThreads: builder.query({
      query: () => '/threads',
      async onQueryStarted(undefined, { dispatch, queryFulfilled }) {
        dispatch(showLoading());
        try {
          const { data } = await queryFulfilled;
          dispatch(hideLoading());
        } catch (err) {
          dispatch(hideLoading());
        }
      },
      transformResponse: (response) => {
        return threadsAdapter.setAll(
          threadsInitialState,
          response.data.threads
        );
      },
      providesTags: (error, arg, result = []) => [
        'Thread',
        ...result.map(({ id }) => ({ type: 'Thread', id: 'LIST' })),
      ],
    }),

    getThread: builder.query({
      query: (threadId) => `/threads/${threadId}`,
      async onQueryStarted(threadId, { dispatch, queryFulfilled }) {
        dispatch(showLoading());
        try {
          const { data } = await queryFulfilled;
          dispatch(hideLoading());
        } catch (err) {
          dispatch(hideLoading());
        }
      },
      transformResponse: (response) => response.data.detailThread,
      transformErrorResponse: (response) => response.data,
      providesTags: (result, error, threadId) => [
        { type: 'Thread', id: threadId },
      ],
    }),

    upvoteThread: builder.mutation({
      query: ({ threadId, newUpvotesBy }) => ({
        url: `threads/${threadId}/up-vote`,
        method: 'POST',
      }),
      async onQueryStarted(
        { threadId, newUpvotesBy },
        { dispatch, queryFulfilled }
      ) {
        const updateInThreads = dispatch(
          threadsApi.util.updateQueryData('getThreads', undefined, (draft) => {
            const thread = draft.entities[threadId];
            if (thread) thread.upVotesBy = newUpvotesBy;
          })
        );
        const updateInThreaddetail = dispatch(
          threadsApi.util.updateQueryData('getThread', threadId, (draft) => {
            draft.upVotesBy = newUpvotesBy;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updateInThreads.undo();
          updateInThreaddetail.undo();
        }
      },
      transformResponse: (response) => response.data.vote,
      invalidatesTags: ['Leaderboard'],
    }),

    downvoteThread: builder.mutation({
      query: ({ threadId, newDownvotesBy }) => ({
        url: `threads/${threadId}/down-vote`,
        method: 'POST',
      }),
      async onQueryStarted(
        { threadId, newDownvotesBy },
        { dispatch, queryFulfilled }
      ) {
        const updateInThreads = dispatch(
          threadsApi.util.updateQueryData('getThreads', undefined, (draft) => {
            const thread = draft.entities[threadId];
            if (thread) thread.downVotesBy = newDownvotesBy;
          })
        );
        const updateInThreaddetail = dispatch(
          threadsApi.util.updateQueryData('getThread', threadId, (draft) => {
            draft.downVotesBy = newDownvotesBy;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updateInThreads.undo();
          updateInThreaddetail.undo();
        }
      },
      transformResponse: (response) => response.data.vote,
      invalidatesTags: ['Leaderboard'],
    }),

    neutralizeThread: builder.mutation({
      query: ({ threadId, newUpvotesBy, newDownvotesBy }) => ({
        url: `threads/${threadId}/neutral-vote`,
        method: 'POST',
      }),
      async onQueryStarted(
        { threadId, newUpvotesBy, newDownvotesBy },
        { dispatch, queryFulfilled }
      ) {
        const updateInThreads = dispatch(
          threadsApi.util.updateQueryData('getThreads', undefined, (draft) => {
            const thread = draft.entities[threadId];
            if (thread) {
              thread.upVotesBy = newUpvotesBy;
              thread.downVotesBy = newDownvotesBy;
            }
          })
        );
        const updateInThreaddetail = dispatch(
          threadsApi.util.updateQueryData('getThread', threadId, (draft) => {
            draft.upVotesBy = newUpvotesBy;
            draft.downVotesBy = newDownvotesBy;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          updateInThreads.undo();
          updateInThreaddetail.undo();
        }
      },
      transformResponse: (response) => response.data.vote,
      invalidatesTags: ['Leaderboard'],
    }),
  }),
});

export const {
  useGetThreadsQuery,
  useGetThreadQuery,
  useCreateThreadMutation,
  useUpvoteThreadMutation,
  useDownvoteThreadMutation,
  useNeutralizeThreadMutation,
} = threadsApi;

const selectThreadsResult = api.endpoints.getThreads.select();

const selectThreadsData = createSelector(
  selectThreadsResult,
  (threadsResult) => threadsResult.data
);

export const { selectAll: selectAllThreads, selectById: selectThreadById } =
  threadsAdapter.getSelectors(
    (state) => selectThreadsData(state) ?? threadsInitialState
  );

export const selectThreadsByCategory = createSelector(
  [selectAllThreads, (state, category) => category],
  (threads, category) =>
    threads.filter((thread) => thread.category === category)
);

export const selectThreadsByCategoryKeyword = createSelector(
  [selectAllThreads, (state, category) => category],
  (threads, category) =>
    threads.filter((thread) => thread.category.includes(category))
);

export const selectThreadsByUser = createSelector(
  [selectAllThreads, (state, ownerId) => ownerId],
  (threads, ownerId) => threads.filter((thread) => thread.ownerId === ownerId)
);
